import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useDomainStore, useFamilyStoriesStore, useReviewQueueStore } from '@/hooks/useStores';
import FamilyPageHeader from '@/components/layout/FamilyPageHeader';
import EditFamilyNameModal from '@/components/family/EditFamilyNameModal';
import SuggestionDiffPreview from '@/components/suggestions/SuggestionDiffPreview';
import SuggestionEvidencePanel from '@/components/suggestions/SuggestionEvidencePanel';
import Spinner from '@/components/shared/Spinner';
import { formatStoryDate } from '@/types/story';
import {
  canAccessReviewQueue,
  isSuggestionConflictError,
  getSuggestionStaleTargetReviewMessage
} from '@/types/suggestion';
import { getUpgradeAwareErrorParts, isFamilyOwner } from '@/utils/familyPlan';

import './family-review-queue-screen.css';
import ScreenHelpDot from '@/components/help/ScreenHelpDot';
import ScreenTourLayer from '@/components/help/ScreenTourLayer';
import { useScreenTour } from '@/hooks/useScreenTour';
import {
  FAMILY_REVIEW_TOUR_STEPS,
  FAMILY_REVIEW_TOUR_STORAGE_KEY,
  resolveFamilyReviewTourSteps
} from '@/data/family-review-tour';

type ReviewFilter = 'all' | 'suggestion' | 'story' | 'media';

const FamilyReviewQueueScreen: React.FC = observer(() => {
  const { t } = useTranslation('screens');
  const { t: tForms } = useTranslation('forms');
  const { t: tOverview } = useTranslation('overview');
  const domainStore = useDomainStore();
  const reviewQueueStore = useReviewQueueStore();
  const familyStoriesStore = useFamilyStoriesStore();
  const navigate = useNavigate();
  const { familyId: familyIdParam } = useParams<{ familyId: string }>();

  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [reviewComment, setReviewComment] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [upgradeHint, setUpgradeHint] = useState<string | null>(null);

  const filterOptions = useMemo(
    () =>
      [
        { id: 'all' as const, label: t('reviewQueue.filterAll') },
        { id: 'suggestion' as const, label: t('reviewQueue.filterSuggestions') },
        { id: 'story' as const, label: t('reviewQueue.filterMemories') },
        { id: 'media' as const, label: t('reviewQueue.filterMedia') }
      ] satisfies { id: ReviewFilter; label: string }[],
    [t]
  );

  const itemTypeLabel = useCallback(
    (itemType: string) => {
      if (itemType === 'suggestion') return tOverview('pendingReview.itemType.suggestion');
      if (itemType === 'story') return tOverview('pendingReview.itemType.story');
      if (itemType === 'media') return tOverview('pendingReview.itemType.media');
      return itemType;
    },
    [tOverview]
  );

  const family = domainStore.currentFamily;
  const familyDisplayName = family?.displayName ?? family?.name ?? t('reviewQueue.familyFallback');
  const canAccess = canAccessReviewQueue(family?.userRole);
  const isOwner = isFamilyOwner(family?.userRole);
  const selectedItem = reviewQueueStore.selectedItem;
  const selectedItemId = selectedItem?.id;
  const selectedItemType = selectedItem?.itemType;
  const selectedSuggestion = reviewQueueStore.selectedSuggestionDetail;

  const loadQueue = useCallback(async () => {
    if (!familyIdParam) return;
    await reviewQueueStore.loadReviewQueue(familyIdParam);
    await reviewQueueStore.loadPendingSummary(familyIdParam);
  }, [familyIdParam, reviewQueueStore]);

  const reviewTour = useScreenTour({
    steps: FAMILY_REVIEW_TOUR_STEPS,
    completedStorageKey: FAMILY_REVIEW_TOUR_STORAGE_KEY,
    resolveSteps: (steps) => resolveFamilyReviewTourSteps(steps, reviewQueueStore.total > 0)
  });

  useEffect(() => {
    if (!familyIdParam) return;
    domainStore.setSelectedFamily(familyIdParam);
  }, [domainStore, familyIdParam]);

  useEffect(() => {
    if (!canAccess) return;
    void loadQueue();
  }, [canAccess, loadQueue, reviewQueueStore.filters.itemType, reviewQueueStore.filters.page]);

  useEffect(() => {
    if (!familyIdParam || !selectedItemId || selectedItemType !== 'suggestion') return;
    void reviewQueueStore.loadSuggestionDetail(familyIdParam, selectedItemId);
  }, [familyIdParam, reviewQueueStore, selectedItemId, selectedItemType]);

  const handleFilterChange = (filter: ReviewFilter) => {
    reviewQueueStore.setFilterItemType(filter);
  };

  const handleSelectItem = (itemId: string) => {
    setActionError(null);
    setUpgradeHint(null);
    setReviewComment('');
    reviewQueueStore.selectItem(itemId);
  };

  const handleReviewAction = async (action: 'approve' | 'reject' | 'request-changes') => {
    if (!familyIdParam || !selectedItem) return;
    setActionError(null);
    setUpgradeHint(null);

    let result;
    if (action === 'approve') {
      result = await reviewQueueStore.approveSelected(familyIdParam, reviewComment);
    } else if (action === 'reject') {
      result = await reviewQueueStore.rejectSelected(familyIdParam, reviewComment);
    } else {
      result = await reviewQueueStore.requestChangesOnSelected(familyIdParam, reviewComment);
    }

    if (result.success) {
      if (selectedItem.itemType === 'story') {
        await familyStoriesStore.searchStories(familyIdParam);
      }
      reviewQueueStore.selectItem(null);
      setReviewComment('');
      await loadQueue();
      return;
    }

    if (action === 'approve' && isSuggestionConflictError(result.error as { status?: number; code?: string })) {
      setActionError(getSuggestionStaleTargetReviewMessage());
      return;
    }

    const parts = getUpgradeAwareErrorParts(
      result.error as Parameters<typeof getUpgradeAwareErrorParts>[0],
      isOwner,
      t('reviewQueue.actionFailed')
    );
    setActionError(parts.message);
    setUpgradeHint(parts.upgradeHint);
  };

  const handleEditNameSuccess = (displayName: string, alternativeName: string | null) => {
    if (!familyIdParam || !domainStore.currentFamily) return;
    domainStore.updateFamilyMetadata({
      ...domainStore.currentFamily,
      id: familyIdParam,
      displayName,
      alternativeName
    });
    setShowEditNameModal(false);
  };

  if (!familyIdParam) {
    return (
      <div className="review-queue-page">
        <p className="review-queue-page__error">{t('reviewQueue.noFamily')}</p>
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="review-queue-page">
        <FamilyPageHeader
          familyDisplayName={familyDisplayName}
          sectionTitle={t('review.title')}
          onEditFamilyName={() => setShowEditNameModal(true)}
          intro={<p className="review-queue-page__intro">{t('review.ledeNoAccess')}</p>}
        />
        <p className="review-queue-page__empty">{t('reviewQueue.noPermission')}</p>
        {showEditNameModal && family && (
          <EditFamilyNameModal
            familyId={familyIdParam}
            currentDisplayName={family.displayName}
            canonicalName={family.name}
            hasAlternative={!!family.alternativeName}
            isOpen={showEditNameModal}
            onClose={() => setShowEditNameModal(false)}
            onSuccess={handleEditNameSuccess}
          />
        )}
      </div>
    );
  }

  return (
    <div className="review-queue-page">
      <FamilyPageHeader
        familyDisplayName={familyDisplayName}
        sectionTitle={t('review.title')}
        onEditFamilyName={() => setShowEditNameModal(true)}
        onStartTour={reviewTour.startTour}
        introTourId="review-intro"
        intro={
          <>
            {t('review.lede')} <ScreenHelpDot />
          </>
        }
      />

      <div className="review-queue-page__filter-bar" data-tour="review-filters">
        <div className="list-seg" role="tablist" aria-label={t('reviewQueue.filtersAria')}>
          {filterOptions.map((option) => {
            const active = reviewQueueStore.filters.itemType === option.id;
            return (
              <button
                key={option.id}
                type="button"
                role="tab"
                aria-selected={active}
                className={`list-seg__btn${active ? ' list-seg__btn--active' : ''}`}
                onClick={() => handleFilterChange(option.id)}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      {reviewQueueStore.error && <p className="review-queue-page__error">{reviewQueueStore.error}</p>}
      {actionError && (
        <div className="review-queue-page__error" role="alert" data-testid="review-action-error">
          <p>{actionError}</p>
          {upgradeHint && <p className="review-queue-page__upgrade-hint">{upgradeHint}</p>}
        </div>
      )}

      <div className="review-queue-page__layout">
        <div className="review-queue-page__list-panel" data-tour="review-list">
          {reviewQueueStore.isLoading ? (
            <div className="review-queue-page__loading">
              <Spinner />
            </div>
          ) : reviewQueueStore.items.length === 0 ? (
            <p className="review-queue-page__empty">{t('reviewQueue.empty')}</p>
          ) : (
            <ul className="review-queue-page__list">
              {reviewQueueStore.items.map((item) => {
                const active = item.id === reviewQueueStore.selectedItemId;
                return (
                  <li key={`${item.itemType}-${item.id}`}>
                    <button
                      type="button"
                      className={`review-queue-page__list-item${active ? ' review-queue-page__list-item--active' : ''}`}
                      onClick={() => handleSelectItem(item.id)}
                    >
                      <span className="review-queue-page__item-type">{itemTypeLabel(item.itemType)}</span>
                      <span className="review-queue-page__item-title">
                        {item.title || item.originalFilename || t('reviewQueue.untitled')}
                      </span>
                      {item.targetLabel && <span className="review-queue-page__item-target">{item.targetLabel}</span>}
                      <span className="review-queue-page__item-date">{formatStoryDate(item.createdAt)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {reviewQueueStore.totalPages > 1 && (
            <div className="review-queue-page__pagination">
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={reviewQueueStore.filters.page <= 1}
                onClick={() => reviewQueueStore.setPage(reviewQueueStore.filters.page - 1)}
              >
                {tForms('actions.previous')}
              </button>
              <span className="review-queue-page__page-label">
                {t('reviewQueue.pageOf', {
                  page: reviewQueueStore.filters.page,
                  total: reviewQueueStore.totalPages
                })}
              </span>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={reviewQueueStore.filters.page >= reviewQueueStore.totalPages}
                onClick={() => reviewQueueStore.setPage(reviewQueueStore.filters.page + 1)}
              >
                {tForms('actions.next')}
              </button>
            </div>
          )}
        </div>

        <div className="review-queue-page__detail-panel" data-tour="review-detail">
          {!selectedItem ? (
            <p className="review-queue-page__detail-placeholder">{t('reviewQueue.selectItem')}</p>
          ) : (
            <>
              <div className="review-queue-page__detail-header">
                <span className="review-queue-page__item-type">{itemTypeLabel(selectedItem.itemType)}</span>
                <h2 className="review-queue-page__detail-title">
                  {selectedItem.title || selectedItem.originalFilename || t('reviewQueue.pendingItem')}
                </h2>
                {selectedItem.targetLabel && (
                  <p className="review-queue-page__detail-target">
                    {t('reviewQueue.about')}{' '}
                    {(selectedItem.familyMemberId ||
                      (selectedItem.itemType === 'suggestion' &&
                        selectedItem.targetType === 'family_member' &&
                        selectedItem.targetId)) &&
                    familyIdParam ? (
                      <Link
                        to={`/families/${familyIdParam}/people/${selectedItem.familyMemberId || selectedItem.targetId}`}
                      >
                        {selectedItem.targetLabel}
                      </Link>
                    ) : (
                      selectedItem.targetLabel
                    )}
                  </p>
                )}
                {selectedItem.summary && <p className="review-queue-page__detail-summary">{selectedItem.summary}</p>}
              </div>

              {selectedItem.itemType === 'suggestion' && (
                <div className="review-queue-page__detail-section">
                  <h3 className="review-queue-page__detail-section-title">{t('reviewQueue.proposedChanges')}</h3>
                  {reviewQueueStore.isLoadingDetail ? (
                    <Spinner />
                  ) : reviewQueueStore.detailError ? (
                    <p className="review-queue-page__error">{reviewQueueStore.detailError}</p>
                  ) : selectedSuggestion ? (
                    <>
                      {selectedSuggestion.description && (
                        <p className="review-queue-page__author-note">{selectedSuggestion.description}</p>
                      )}
                      <SuggestionDiffPreview diffPayload={selectedSuggestion.diffPayload} />
                      <SuggestionEvidencePanel suggestion={selectedSuggestion} />
                    </>
                  ) : null}
                </div>
              )}

              {selectedItem.itemType === 'story' && selectedItem.familyMemberId && (
                <div className="review-queue-page__detail-section">
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => navigate(`/families/${familyIdParam}/memories/${selectedItem.id}`)}
                  >
                    {t('reviewQueue.openMemory')}
                  </button>
                </div>
              )}

              {selectedItem.itemType === 'media' && (
                <div className="review-queue-page__detail-section">
                  <p className="review-queue-page__media-meta">
                    {selectedItem.mimeType}
                    {selectedItem.originalFilename ? ` · ${selectedItem.originalFilename}` : ''}
                  </p>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => navigate(`/families/${familyIdParam}/media`)}
                  >
                    {t('reviewQueue.openMediaLibrary')}
                  </button>
                </div>
              )}

              <div className="review-queue-page__review-form">
                <label htmlFor="review-comment" className="review-queue-page__comment-label">
                  {t('reviewQueue.commentOptional')}
                </label>
                <textarea
                  id="review-comment"
                  className="review-queue-page__comment-input"
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  disabled={reviewQueueStore.isReviewing}
                />

                <div className="review-queue-page__review-actions">
                  {selectedItem.itemType === 'suggestion' && (
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      disabled={reviewQueueStore.isReviewing}
                      onClick={() => void handleReviewAction('request-changes')}
                    >
                      {tForms('actions.requestChanges')}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled={reviewQueueStore.isReviewing}
                    onClick={() => void handleReviewAction('reject')}
                  >
                    {tForms('actions.reject')}
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary btn--sm"
                    disabled={reviewQueueStore.isReviewing}
                    onClick={() => void handleReviewAction('approve')}
                  >
                    {reviewQueueStore.isReviewing ? tForms('actions.submitting') : tForms('actions.approve')}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {showEditNameModal && family && (
        <EditFamilyNameModal
          familyId={familyIdParam}
          currentDisplayName={family.displayName}
          canonicalName={family.name}
          hasAlternative={!!family.alternativeName}
          isOpen={showEditNameModal}
          onClose={() => setShowEditNameModal(false)}
          onSuccess={handleEditNameSuccess}
        />
      )}

      <ScreenTourLayer tour={reviewTour} />
    </div>
  );
});

export default FamilyReviewQueueScreen;
